import { CellClickedEvent, ColDef } from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import logo from '@assets/logo.svg';
import { Box } from '@atgfr8/atg-ui-tailwind';
import { Button, Notification } from '@components';
import { getRowGroupStyle } from '@components/Grid/utils';
import { getDB } from '@database';
import { useNavigate } from '@solidjs/router';
import { HoldsInfo } from '@store/holdsTab';
import {
  deleteFlag,
  setWarningDataStore,
  warningDataStore,
} from '@store/index';
import { dateFormatter } from '@store/loadboard';
import { CACHE_DURATION } from '@utils/cacheUtils';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { routeOrderDetails } from '@utils/routeOrderDetails';
import { createSignal, Setter } from 'solid-js';
import '../../Grid/AgGridStyles.css';

const agGridCellStyle = {
  padding: '5px 5px',
  'word-break': 'break-word',
  'white-space': 'normal',
  'line-height': '1',
  'align-items': 'center !important',
};

const commonColumns: ColDef[] = [
  {
    field: 'orderId',
    headerName: 'Order',
    minWidth: 90,
    maxWidth: 110,
    filter: false,
    cellStyle: agGridCellStyle,
  },
  {
    field: 'customer',
    headerName: 'Customer',
    filter: 'agTextColumnFilter',
    minWidth: 130,
    maxWidth: 250,
    autoHeight: true,
    cellStyle: agGridCellStyle,
  },
  {
    field: 'agent',
    headerName: 'Set By',
    filter: 'agTextColumnFilter',
    minWidth: 90,
    maxWidth: 150,
    autoHeight: true,
    cellStyle: agGridCellStyle,
  },
  {
    field: 'date',
    headerName: 'Date',
    filter: 'agDateColumnFilter',
    filterParams: {
      minValidYear: 1900,
      maxValidYear: new Date().getFullYear() + 1,
    },
    maxWidth: 110,
    minWidth: 110,
    valueFormatter: dateFormatter,
  },
  {
    field: 'description',
    headerName: 'Details',
    filter: 'agTextColumnFilter',
    minWidth: 150,
    maxWidth: 350,
    autoHeight: true,
    cellStyle: agGridCellStyle,
  },
  {
    field: 'Action',
    headerName: 'Action',
    filter: 'agTextColumnFilter',
    colId: 'Action',
    minWidth: 90,
    autoHeight: true,
    cellStyle: agGridCellStyle,
    cellRenderer: (params: { data: HoldsInfo }) => {
      return (
        <Button
          label="Release Hold"
          color="primary"
          size="sm"
          sx={{ 'margin-left': '10px' }}
          onClick={async () => {
            setLoading(String(params.data.id));
            await deleteFlag(params.data.id as number);
            await updateBillingHolds(params.data.id as number);
            setLoading('');
          }}
          isLoading={loading() === String(params.data.id)}
          dataTestId={formatDataTestId('Release Hold', 'button')}
        />
      );
    },
  },
];

const [loading, setLoading] = createSignal('');

const updateBillingHolds = async (id: number) => {
  const db = getDB();
  if (!db) return;

  const key = 'billing-holds';
  const storeEntry = await db.billingHolds.get(key);
  if (!storeEntry) return;

  const filtered = storeEntry.items.filter((load) => load.id !== id);

  await db.billingHolds.put({
    key,
    cacheDate: Date.now(),
    cacheDuration: CACHE_DURATION.ONE_DAY,
    items: filtered,
  });

  setWarningDataStore('billingHolds', 'data', filtered);
};

const BillingHoldsWarningGrid = (props: { setActiveTab: Setter<number> }) => {
  const navigate = useNavigate();
  let gridRef: AgGridSolidRef | undefined;
  const imageTag = `<img src=${logo} alt="" />`;
  return (
    <>
      <Notification
        sxProps="bg-white! text-sm!"
        type="info"
        text={
          <span class="text-sm">
            These are on Billing Hold. If they can be processed, please release
            the billing hold
          </span>
        }
      />
      <Box class="ag-theme-alpine flex flex-col flex-1! h-[82%]! ">
        <AgGridSolid
          ref={gridRef}
          columnDefs={commonColumns}
          rowData={warningDataStore.billingHolds.data}
          gridOptions={{
            defaultColDef: {
              flex: 1,
              filter: false,
            },
          }}
          getRowStyle={getRowGroupStyle}
          overlayLoadingTemplate={imageTag}
          noRowsOverlayComponent={imageTag}
          onCellClicked={(e: CellClickedEvent) => {
            if (e.colDef.colId !== 'Action') {
              const orderId = (e.data as { orderId: number }).orderId;
              if (routeOrderDetails(orderId)) {
                props.setActiveTab(-1);
                navigate(`/order/details/${orderId}`);
              }
            }
          }}
        />
      </Box>
    </>
  );
};

export default BillingHoldsWarningGrid;
